'use client'

import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/utils'

type TableProps = {
  freezeFirstColumn?: boolean
}

const Table = ({
  className,
  freezeFirstColumn,
  ...props
}: React.ComponentProps<'table'> & TableProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [showFirstColumnShadow, setShowFirstColumnShadow] = useState(false)

  useEffect(() => {
    if (!freezeFirstColumn) return

    const container = containerRef.current
    if (!container) return

    const updateStickyShadow = () => {
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth
      const isScrollMoved = container.scrollLeft > 0
      setShowFirstColumnShadow(hasHorizontalScroll && isScrollMoved)
    }

    updateStickyShadow()
    container.addEventListener('scroll', updateStickyShadow)
    window.addEventListener('resize', updateStickyShadow)

    return () => {
      container.removeEventListener('scroll', updateStickyShadow)
      window.removeEventListener('resize', updateStickyShadow)
    }
  }, [freezeFirstColumn])

  return (
    <div
      ref={containerRef}
      data-slot="table-container"
      data-testid="table-container"
      className={cn(
        'text-2xs relative w-full overflow-x-auto rounded-lg border-2 border-gray-200',
        freezeFirstColumn && 'freeze-first-column',
        showFirstColumnShadow && 'freeze-column-shadow',
      )}
      // Smooths the transition between vertical and horizontal scroll in Firefox
      // when scrolls are chained.
      onWheel={(e: React.WheelEvent<HTMLDivElement>) => {
        e.stopPropagation()
      }}
    >
      <table
        data-slot="table"
        className={cn('text-2xs w-full', className)}
        {...props}
      />
    </div>
  )
}

const TableHeader = ({
  className,
  ...props
}: React.ComponentProps<'thead'>) => {
  return (
    <thead
      data-slot="table-header"
      className={cn('bg-gray-200', className)}
      {...props}
    />
  )
}

const TableBody = ({ className, ...props }: React.ComponentProps<'tbody'>) => {
  return (
    <tbody
      data-slot="table-body"
      className={cn(
        '[&_tr:nth-child(even)]:bg-gray-200 [&_tr:nth-child(odd)]:bg-white',
        className,
      )}
      {...props}
    />
  )
}

const TableRow = ({ className, ...props }: React.ComponentProps<'tr'>) => {
  return <tr data-slot="table-row" className={cn('', className)} {...props} />
}

const TableHead = ({ className, ...props }: React.ComponentProps<'th'>) => {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'h-10 min-w-[170px] whitespace-nowrap px-4 py-2 text-left align-middle',
        className,
      )}
      {...props}
    />
  )
}

const TableCell = ({ className, ...props }: React.ComponentProps<'td'>) => {
  return (
    <td
      data-slot="table-cell"
      className={cn('h-10 whitespace-nowrap px-4 py-2 align-middle', className)}
      {...props}
    />
  )
}

export { Table, TableHeader, TableBody, TableHead, TableRow, TableCell }
